import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import GenericFormDrawer from '../../../components/GenericFormDrawer';
import Button from '../../../components/ui/Button';
import api from '../../../services/api';
import { formatDate, formatDateTime } from '../../../utils/Utils';
import { canExempt, canRemoveExemption } from '../../../components/performance/ExemptionDrawer';
import {
  assigneeText, canRemind, stageLabel, statusLabel, taskLabel,
} from '../config/tasks';

// Spec §7.4, §3.5 — görev detay çekmecesi (yalnız İK): GET v1/period-stage-tasks/{id}. Salt okunur bilgi + işlemler (Hatırlat,
// Muaf Tut / Muafiyeti Kaldır — çekmeceleri çağıran sekme sahiplenir). "Çalışana Git" mevcut çalışan detayına gider (E14 Faz 3'te).
// api.js GET'te reject etmez: yüklenemedi durumu res.data.status'tan ayrılır ("boş" gibi görünmez).
const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const MetaLabel = styled.span`
  min-width: 170px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 0;
  overflow-wrap: anywhere;
`;

const SectionTitle = styled.h4`
  margin: 20px 0 10px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

export default function TaskDetailDrawer({
  taskId, open, onClose, reloadKey, onRemind, onExempt, onRemoveExemption,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [state, setState] = useState({ loading: false, error: false, data: null });

  useEffect(() => {
    if (!open || !taskId) { setState({ loading: false, error: false, data: null }); return undefined; }
    let alive = true;
    setState({ loading: true, error: false, data: null });
    api.get(`/v1/period-stage-tasks/${taskId}`).then((res) => {
      if (!alive) return;
      if (!res?.data?.status) setState({ loading: false, error: true, data: null });
      else setState({ loading: false, error: false, data: res.data.data });
    });
    return () => { alive = false; };
  }, [open, taskId, reloadKey]);

  const d = state.data;
  const date = (v) => (v ? formatDate(v, lang) : '-');
  const row = (label, value, attr) => (
    <MetaRow data-field={attr}>
      <MetaLabel>{label}</MetaLabel>
      <MetaValue>{value ?? '-'}</MetaValue>
    </MetaRow>
  );
  const c = (key) => t(`performance.processHealth.detail.${key}`);
  // Detay DTO'su görev satırının alan adlarını taşır (taskId, participantId, stageKey, status, exemptAt) — çekmeceler doğrudan kullanır.
  const asRow = d;

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={d ? taskLabel(t, d.taskKey) : t('performance.processHealth.detail.title')}
      schema={[]}
      submitLabel={t('performance.processHealth.detail.close')}
      onSave={async () => true}
    >
      {state.loading ? <Muted data-state="loading">{t('common.loading')}</Muted> : null}
      {state.error ? <Muted data-state="error">{t('performance.operations.panel.error')}</Muted> : null}
      {d ? (
        <div data-detail={d.taskId}>
          {row(c('employee'), (
            <>
              {d.employeeName || '-'}
              {' · '}
              <Link to={`/performance/profile/${d.employeeId}?periodId=${d.periodId}`} data-link="employee">{c('goToEmployee')}</Link>
            </>
          ), 'employee')}
          {row(c('stage'), stageLabel(t, d.stageKey), 'stage')}
          {row(c('status'), statusLabel(t, d.status), 'status')}
          {row(c('assignee'), `${assigneeText(t, d)}${d.assigneeTitle ? ` · ${d.assigneeTitle}` : ''}`, 'assignee')}
          {row(c('assigneeRole'), d.assigneeRole ? t(`performance.reminder.role.${d.assigneeRole}`, { defaultValue: d.assigneeRole }) : '-', 'role')}
          {row(c('delegate'), d.delegateName || '-', 'delegate')}
          {row(c('firstDueAt'), date(d.firstDueAt), 'firstDueAt')}
          {row(c('dueAt'), `${date(d.dueAt)}${d.overdueDays ? ` · ${t('performance.operations.days', { count: d.overdueDays })}` : ''}`, 'dueAt')}
          {row(c('openedAt'), date(d.openedAt), 'openedAt')}
          {row(c('firstCompletedAt'), date(d.firstCompletedAt), 'firstCompletedAt')}
          {row(c('completedAt'), date(d.completedAt), 'completedAt')}
          {row(c('reopenCount'), String(d.reopenCount ?? 0), 'reopenCount')}
          {d.historyComplete ? null : <Muted data-note="history">{c('historyIncomplete')}</Muted>}

          {d.exemptAt ? (
            <>
              <SectionTitle>{c('exemption')}</SectionTitle>
              {row(c('reason'), t(`performance.period.participants.reasons.${d.exemptReasonCode}`, { defaultValue: d.exemptReasonCode }), 'exemptReason')}
              {row(c('note'), d.exemptNote || '-', 'exemptNote')}
              {row(c('exemptBy'), d.exemptByName || '-', 'exemptBy')}
              {row(c('exemptAt'), formatDateTime(d.exemptAt, lang), 'exemptAt')}
            </>
          ) : null}

          <SectionTitle>{c('reminders')}</SectionTitle>
          {(d.reminders || []).length === 0 ? <Muted data-state="no-reminders">{c('noReminders')}</Muted> : (d.reminders || []).map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Item key={`${r.at}-${i}`} data-reminder={i}>
              <span>{formatDateTime(r.at, lang)}</span>
              <span>{t(`performance.reminder.role.${r.senderRole}`, { defaultValue: r.senderRole })}</span>
              <span>{t(`performance.reminder.outcome.${r.outcome}`, { defaultValue: r.outcome })}</span>
            </Item>
          ))}

          <Buttons>
            {canRemind(asRow) ? (
              <Button variant="outline" data-action="remind" onClick={() => onRemind?.(asRow)}>{t('performance.operations.rowAction.remind')}</Button>
            ) : null}
            {canExempt(asRow) ? (
              <Button variant="outline" data-action="exempt" onClick={() => onExempt?.(asRow)}>{t('performance.operations.rowAction.exempt')}</Button>
            ) : null}
            {canRemoveExemption(asRow) ? (
              <Button variant="outline" data-action="remove-exemption" onClick={() => onRemoveExemption?.(asRow)}>
                {t('performance.processHealth.rowAction.removeExemption')}
              </Button>
            ) : null}
          </Buttons>
        </div>
      ) : null}
    </GenericFormDrawer>
  );
}
